import { announcementApi } from "@/main/api";
import Box from "@/main/components/shared/box";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogKeyboardDatePickerContainer from "@/main/components/shared/datepicker/keyboardDatePickerContainer";
import KOGFileUploader from "@/main/components/shared/fileUploader/fileUploader";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputAutoCompleteCheckControlled from "@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import { KOGAreaButtons } from "@/main/components/shared/styles";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import { KeyValue } from "@/main/models/common";
import { AnnouncementSaveRequest } from "@/main/models/request/announcementRequest";
import { AnnouncementDetailResponse, AnnouncementFileResponse } from "@/main/models/response/announcementResponse";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { AnnouncementStatusEnum } from "@/main/services/enum/AnnouncementStatusEnum";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { getTranslateByKey } from "@/main/services/utils";
import { finalDateForm, initalDateForm, TypeKeysValidationsDate, validateFormDate } from "@/main/services/utilsDate";
import { downloadPdfFile, toBase64 } from "@/main/services/utilsFile";
import { userIsAdmin, userIsInsurer } from "@/main/services/utilsUser";
import {
	getDomainAnnouncements,
	getDomainCompanyAnnouncers,
	getDomainWhoCanSee,
	getStatusDomain,
	useAnnouncementReducer,
} from "@/main/store/announcement";
import { resetReducerCompany } from "@/main/store/company";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { getSystemApplication, useSystemApplicationReducer } from "@/main/store/systemApplication";
import { Checkbox, FormControlLabel } from "@mui/material";
import { isAfter } from "date-fns";
import { isNil } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AnnouncementCopyModal } from "../announcementCopyModal";
import { AnnouncementRegisterCompaniesWillSee } from "./announcementRegisterCompaniesWillSee";
import { AnnouncementRegisterPreview } from "./announcementRegisterPreview";
import { AnnouncementRegisterWarnning } from "./announcementRegisterWarnning";
import * as S from "./styles";

const ID_BASE_COMPONENTS = "id-registerAnnouncement";
const ignoreKeyValidations: TypeKeysValidationsDate[] = ["finalDateGreaterThanCurrentDate"];

export function AnnouncementRegisterScreen() {
	const [searchParams, setSearchParams] = useSearchParams();
	const guidAnnouncementParams = searchParams.get("announcementGuid");

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoadingSave, setIsLoadingSave] = useState(false);
	const [isLoadingDetail, setIsLoadingDetail] = useState(false);
	const [announcementFile, setAnnouncementFile] = useState<File[]>([]);
	const [isEdit, setIsEdit] = useState(false);
	const [companiesSelected, setCompaniesSelected] = useState<string[]>([]);
	const [fileData, setFileData] = useState<AnnouncementFileResponse | null>(null);
	const [announcementDetail, setAnnouncementDetail] = useState<AnnouncementDetailResponse>();
	const [isOpenModalCopy, setIsOpenModalCopy] = useState(false);
	const [isOpenModalPreview, setIsOpenModalPreview] = useState(false);

	const systemApplicationReducer = useSystemApplicationReducer();
	const announcementReducer = useAnnouncementReducer();
	const countryReducer = useCountryReducer();
	const companyTypeReducer = useCompanyTypeReducer();

	const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.MaximumCreateAnnouncementDaysInterval);

	const INITIAL_FORM_STATE = {
		name: "" as string,
		systemApplication: null as KeyValue | null,
		whoCanSee: [] as KeyValue[] | null,
		startDate: initalDateForm(),
		finalDate: ((d) => new Date(d.setDate(d.getDate() - 1)))(new Date(finalDateForm(parameterRangeDays))),
		announcementStatus: null as KeyValue | null,
		dontShowMeAgain: false as boolean,
		acceptMessageContent: false as boolean,
		country: null as KeyValue | null,
		company: null as KeyValue | null,
		companyType: null as KeyValue | null,
	};

	type IAnnoucementoRegisterForm = typeof INITIAL_FORM_STATE;

	const {
		control,
		setValue,
		formState: { errors },
		watch,
		handleSubmit,
	} = useForm<IAnnoucementoRegisterForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	const watcherWhoCanSee = watch("whoCanSee");
	const watcherCompany = watch("company");
	const watcherFinalDate = watch("finalDate");
	const watcherCountry = watch("country");
	const watcherCompanyType = watch("companyType");

	useEffect(() => {
		dispatch(getSystemApplication());
		dispatch(getDomainAnnouncements());
		dispatch(getDomainWhoCanSee());
		dispatch(getCountriesUser());
		dispatch(getCompanyType());

		if (!guidAnnouncementParams) {
			dispatch(getStatusDomain());
		}

		return () => {
			dispatch(resetReducerCompany());
		};
	}, []);

	useEffect(() => {
		if (!isNil(guidAnnouncementParams)) {
			setIsEdit(true);
			loadDetails();
		}
	}, [guidAnnouncementParams]);

	useEffect(() => {
		if (!isNil(watcherCompany)) {
			dispatch(getDomainAnnouncements(watcherCompany.key.toString()));
		}
	}, [watcherCompany]);

	useEffect(() => {
		if (!isNil(watcherCountry)) {
			dispatch(
				getDomainCompanyAnnouncers({
					guidCountry: watcherCountry.key.toString(),
					idCompanyType: !isNil(watcherCompanyType) ? (watcherCompanyType.key as number) : undefined,
				}),
			);
		}
	}, [watcherCountry, watcherCompanyType]);

	const optionWhoCanSee = useMemo(() => {
		return (
			announcementReducer.domainWhoCanSee.data?.map((item) => ({
				key: item.key,
				value: item.value,
			})) || []
		);
	}, [announcementReducer.domainWhoCanSee.data, announcementReducer.domainWhoCanSee.isLoading]);

	const disabledDropStatus = useMemo(() => {
		const currentDate = new Date();
		return isEdit && watcherFinalDate < currentDate;
	}, [isEdit, watcherFinalDate]);

	const disabledAllFields = useMemo(() => {
		if (!isEdit) return false;

		if (!isNil(announcementDetail)) {
			const { idAnnouncementStatus, startDate } = announcementDetail;

			if (idAnnouncementStatus == AnnouncementStatusEnum.Expired) {
				return true;
			}

			const currentDate = new Date().setHours(0, 0, 0, 0);
			const startDateValue = new Date(startDate);

			if (idAnnouncementStatus == AnnouncementStatusEnum.Draft || isAfter(startDateValue, currentDate)) {
				return false;
			}
		}

		return true;
	}, [announcementDetail, isEdit]);

	const disableDropStatusAndGridCompanies = useMemo(() => {
		if (!isNil(announcementDetail)) {
			const { idAnnouncementStatus } = announcementDetail;

			const refStatus = [AnnouncementStatusEnum.Active, AnnouncementStatusEnum.Suspended];

			if (refStatus.includes(idAnnouncementStatus)) return false;

			return disabledAllFields;
		}

		return disabledAllFields;
	}, [announcementDetail, disabledAllFields]);

	const handleBack = () => {
		const previousPath = location.state?.from;

		if (previousPath) {
			navigate(previousPath);
			return;
		}

		navigate(RoutesConstants.registrationSearchAnnouncement, { replace: true });
	};

	function loadDetails() {
		if (isNil(guidAnnouncementParams)) return;

		setIsLoadingDetail(true);

		announcementApi
			.getDetail(guidAnnouncementParams)
			.then((response) => {
				const result = response.data.result;

				setAnnouncementDetail(result);

				setValue("name", result.name);
				setValue("startDate", new Date(result.startDate));
				setValue("finalDate", new Date(result.endDate));
				setValue("dontShowMeAgain", result.displayDontShowMeAgain);
				setValue("acceptMessageContent", result.displayAcceptMessageContent);
				setValue("whoCanSee", result.whoCanSeeCompanyTypes);

				setValue("systemApplication", {
					key: result.idSystemApplication,
					value: result.nameSystemApplication,
				});

				setValue("announcementStatus", {
					key: result.idAnnouncementStatus,
					value: result.nameAnnouncementStatus,
				});

				setValue("company", {
					key: result.guidCompany,
					value: result.companyName,
				});

				setValue("companyType", {
					key: result.idCompanyType,
					value: result.nameCompanyType,
				});

				setValue("country", {
					key: result.guidCountry,
					value: result.countryName,
				});

				dispatch(getStatusDomain(result.idAnnouncementStatus == AnnouncementStatusEnum.Expired));
			})
			.finally(() => setIsLoadingDetail(false));

		announcementApi.getFile(guidAnnouncementParams).then((response) => {
			const byteArray = Uint8Array.from(atob(response.data.result.base64), (c) => c.charCodeAt(0));

			const file = new File([byteArray], response.data.result.fileName, {
				type: "application/pdf",
			});

			setFileData(response.data.result);
			setAnnouncementFile([file]);
		});
	}

	function handleSubmitSave(data: IAnnoucementoRegisterForm) {
		validateFormDate(data.startDate, data.finalDate, parameterRangeDays, ignoreKeyValidations);

		const request: AnnouncementSaveRequest = {
			guid: guidAnnouncementParams ?? undefined,
			name: data.name,
			idSystemApplication: Number(data.systemApplication?.key),
			whoCanSeeCompanyTypeIds: data.whoCanSee?.map((item) => Number(item.key)) || [],
			startDate: data.startDate,
			endDate: data.finalDate,
			idAnnouncementStatus: Number(data.announcementStatus?.key),
			displayDontShowMeAgain: data.dontShowMeAgain,
			displayAcceptMessageContent: data.acceptMessageContent,
			guidCompany: data.company ? String(data.company.key) : "",
			guidCompaniesToRemove: companiesSelected,
		};

		setIsLoadingSave(true);
		announcementApi
			.save(request, disabledAllFields ? null : announcementFile[0])
			.then((response) => {
				const messageSuccess = isEdit
					? getTranslateByKey("information.announcementEditSuccessfully")
					: getTranslateByKey("information.announcementCreateSuccessfully");

				toast.success(messageSuccess);

				if (isEdit) {
					loadDetails();
					setCompaniesSelected([]);
					return;
				}

				setIsEdit(true);
				setSearchParams({ announcementGuid: response.data.result }, { replace: true });
			})
			.finally(() => setIsLoadingSave(false));
	}

	async function handleDownloadFile(file: File) {
		const base64 = (await toBase64(file)) as string;
		const base64Formated = base64.replace("data:application/pdf;base64,", "");
		downloadPdfFile(base64Formated, file.name.concat(".pdf"));
	}

	function isDisabledCountry() {
		return countryReducer.countriesUser.data?.length === 1;
	}

	const companyTypeReducerFilter = useMemo(() => {
		const allowedTypes = new Set<CompanyTypeEnum>();

		if (userIsAdmin()) {
			allowedTypes.add(CompanyTypeEnum.Admin);
			allowedTypes.add(CompanyTypeEnum.Insurer);
		} else if (userIsInsurer()) {
			allowedTypes.add(CompanyTypeEnum.Insurer);
		}

		return (
			companyTypeReducer.companyType.data?.filter((item) => allowedTypes.has(Number(item.key) as CompanyTypeEnum)) ?? []
		);
	}, [companyTypeReducer.companyType.data]);

	if (isLoadingDetail) return <LoadingComponent />;

	return (
		<>
			<S.Container>
				<S.HeaderText>
					{getTranslateByKey("information.registration")}
					{" > "}
					{getTranslateByKey("information.registerAnnouncement")}
				</S.HeaderText>

				<AnnouncementRegisterWarnning announcement={announcementDetail} />

				<Box title={getTranslateByKey("information.fields")}>
					<S.RowInputs>
						<KogAutoCompleteControlled
							label={getTranslateByKey("information.country")}
							control={control}
							nameCustom="country"
							rulesCustom={{ required: true }}
							errorMessageCustom={
								errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
							}
							hasErrorCustom={!!errors.country}
							options={countryReducer.countriesUser.data}
							isLoading={countryReducer.countriesUser.isLoading}
							onChangeCustom={() => {}}
							disabled={isDisabledCountry()}
							autoHighlightCustom={countryReducer.countriesUser.data?.length === 1}
							setValueCustom={setValue}
							id={`${ID_BASE_COMPONENTS}-select-country`}
						/>

						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-companyType`}
							nameCustom="companyType"
							label={getTranslateByKey("information.companyType")}
							isDefaultValueUnique
							control={control}
							setValueCustom={setValue}
							options={companyTypeReducerFilter}
							isLoading={companyTypeReducer.companyType.isLoading}
							disabled={companyTypeReducerFilter.length <= 1}
							rulesCustom={{ required: true }}
							onChangeCustom={() => {
								setValue("company", null);
							}}
						/>

						<KogAutoCompleteControlled
							disableVirtualization
							label={getTranslateByKey("information.company")}
							isDefaultValueUnique
							control={control}
							nameCustom="company"
							rulesCustom={{ required: true }}
							errorMessageCustom=""
							hasErrorCustom={false}
							options={announcementReducer.domainCompanyAnnouncers.data}
							isLoading={announcementReducer.domainCompanyAnnouncers.isLoading}
							setValueCustom={setValue}
							id={`${ID_BASE_COMPONENTS}-select-company`}
							onChangeCustom={() => {}}
							disabled={
								disabledAllFields ||
								isNil(watcherCompanyType) ||
								announcementReducer.domainCompanyAnnouncers.data?.length === 1
							}
						/>

						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-system`}
							label={getTranslateByKey("information.systemApplication")}
							control={control}
							nameCustom="systemApplication"
							rulesCustom={{ required: true }}
							errorMessageCustom=""
							hasErrorCustom={false}
							options={systemApplicationReducer.systemApplication.data}
							isLoading={systemApplicationReducer.systemApplication.isLoading}
							onChangeCustom={() => {}}
							setValueCustom={setValue}
						/>

						<KogKeyboardDatePickerContainer
							idScreen={`${ID_BASE_COMPONENTS}-datePicker-startDate-finalDate`}
							nameStartDate="startDate"
							nameFinalDate="finalDate"
							parameterRangeDays={parameterRangeDays}
							control={control}
							disabledFields={disabledAllFields ? ["startDate", "finalDate"] : []}
							ignoreKeyValidations={ignoreKeyValidations}
						/>

						<KogInputTextControlledNew
							name="name"
							label={getTranslateByKey("information.name")}
							control={control}
							inputProps={{ maxLength: 100 }}
							controllerProps={{ rules: { required: true } }}
							error={!!errors.name}
							id={`${ID_BASE_COMPONENTS}-txt-name`}
							disabled={disabledAllFields}
						/>

						<KogInputAutoCompleteCheckControlled
							label={getTranslateByKey("information.whoCanSee")}
							name="whoCanSee"
							control={control}
							setValueCustom={setValue}
							options={optionWhoCanSee}
							isLoading={announcementReducer.domainWhoCanSee.isLoading}
							onChangeCustom={() => {}}
							id={`${ID_BASE_COMPONENTS}-txt-whoCanSee`}
							disableSelectAll
							disabled={disabledAllFields}
							controllerProps={{ rules: { required: true } }}
							customInputProps={{ required: true }}
						/>

						<KogAutoCompleteControlled
							label={getTranslateByKey("information.status")}
							nameCustom="announcementStatus"
							errorMessageCustom=""
							control={control}
							setValueCustom={setValue}
							options={announcementReducer.statusDomain.data}
							isLoading={announcementReducer.statusDomain.isLoading}
							rulesCustom={{ required: true }}
							hasErrorCustom={false}
							onChangeCustom={() => {}}
							id={`${ID_BASE_COMPONENTS}-txt-statusRegister`}
							disabled={disableDropStatusAndGridCompanies}
						/>

						<S.Checkboxes>
							<TooltipComponent title={getTranslateByKey("information.dontShowMeAgainTooltip")} placement="top">
								<span>
									<Controller
										name="dontShowMeAgain"
										control={control}
										render={({ field: { onChange, value } }) => (
											<FormControlLabel
												disabled={disabledDropStatus}
												label={
													<div
														style={{
															display: "flex",
															gap: 10,
															alignItems: "center",
														}}
													>
														<span>{getTranslateByKey("information.dontShowMeAgain")}</span>
													</div>
												}
												control={
													<Checkbox
														color="primary"
														checked={value}
														onChange={(e) => {
															onChange(e.target.checked);
														}}
														id={`${ID_BASE_COMPONENTS}-checkbox-dontShowMeAgain`}
													/>
												}
											/>
										)}
									/>
								</span>
							</TooltipComponent>

							<TooltipComponent title={getTranslateByKey("information.acceptMessageContentTooltip")} placement="top">
								<span>
									<Controller
										name="acceptMessageContent"
										control={control}
										render={({ field: { onChange, value } }) => (
											<FormControlLabel
												disabled={disabledDropStatus}
												label={
													<div
														style={{
															display: "flex",
															gap: 10,
															alignItems: "center",
														}}
													>
														<span>{getTranslateByKey("information.acceptMessageContent")}</span>
													</div>
												}
												control={
													<Checkbox
														color="primary"
														checked={value}
														onChange={(e) => {
															onChange(e.target.checked);
														}}
														id={`${ID_BASE_COMPONENTS}-checkbox-acceptMessageContent`}
													/>
												}
											/>
										)}
									/>
								</span>
							</TooltipComponent>
						</S.Checkboxes>
					</S.RowInputs>

					<S.ContainerFile>
						<KOGFileUploader
							isFullScreenDragDrop
							isRequired
							files={announcementFile}
							setFiles={setAnnouncementFile}
							maxFiles={1}
							typeFiles={["pdf"]}
							downloadFile={fileData ? handleDownloadFile : undefined}
						/>
					</S.ContainerFile>
				</Box>

				{!isNil(guidAnnouncementParams) && (
					<AnnouncementRegisterCompaniesWillSee
						guidAnnouncement={guidAnnouncementParams}
						companiesSelected={companiesSelected}
						setCompaniesSelected={setCompaniesSelected}
						watcherWhoCanSee={watcherWhoCanSee}
						watcherCompany={watcherCompany}
						disabled={disableDropStatusAndGridCompanies}
					/>
				)}

				<KOGAreaButtons $isRegisterScreen style={{ marginTop: 30 }}>
					{isEdit && (
						<SecondaryButton
							onClick={() => setIsOpenModalCopy(true)}
							id={`${ID_BASE_COMPONENTS}-button-duplicationConfiguration`}
						>
							{getTranslateByKey("information.duplicateConfiguration")}
						</SecondaryButton>
					)}

					{!isNil(fileData) && (
						<SecondaryButton
							onClick={() => setIsOpenModalPreview(true)}
							id={`${ID_BASE_COMPONENTS}-button-previewAnnouncement`}
						>
							{getTranslateByKey("information.preview")}
						</SecondaryButton>
					)}

					<SecondaryButton onClick={handleBack} id={`${ID_BASE_COMPONENTS}-button-back`}>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<Button
						id={`${ID_BASE_COMPONENTS}-button-save-annoucement`}
						type="submit"
						isLoading={isLoadingSave}
						onClick={handleSubmit(handleSubmitSave)}
					>
						{getTranslateByKey("information.save")}
					</Button>
				</KOGAreaButtons>
			</S.Container>

			{!isNil(announcementDetail) && (
				<AnnouncementCopyModal
					announcement={announcementDetail}
					openModal={isOpenModalCopy}
					setOpenModal={setIsOpenModalCopy}
				/>
			)}

			{!isNil(fileData) && !isNil(announcementDetail) && (
				<AnnouncementRegisterPreview
					announcement={announcementDetail}
					base64={fileData.base64}
					open={isOpenModalPreview}
					setOpen={setIsOpenModalPreview}
				/>
			)}
		</>
	);
}
